import * as React from 'react';
import DataGrid from 'devextreme-react/data-grid';
import { withStyles } from '@material-ui/core/styles';
import { ISerialDialogProps } from './AddSerialDialog.properties';
import styles from './AddSerialDialog.styles';
import FormViewModal from 'components/common/Modals/FormViewModal';
import { IActionItem } from 'components/common/Modals/FormViewModal.properties';
import TextField from '@markinson/uicomponents-v2/TextField';
import CheckCircleIcon from '@markinson/uicomponents-v2/SvgIcons/CheckCircle';
import { Cancel as CancelIcon } from '@markinson/uicomponents-v2/SvgIcons';
import { Button as MUIButton, Table, TableBody, TableRow, TableCell } from '@material-ui/core';
import { IBinLotFacade, } from 'api/swaggerTypes';
import classNames from 'classnames';

import { isNull } from 'utils/utils';
import { ENTER_KEY } from 'utils/constants';
import SerialNumber from './SerialNumber';
import BasicLookupActionField from 'components/FormView/Fields/BasicLookupActionField/BasicLookupActionField';
import DynamicDataGrid from '../DataGridDevEx/DynamicDataGrid';
import { columns } from './constants';
import { nonBinTrackedColumns } from './nonbinTracked.constants';
import { createDataSource } from '../DataGridDevEx/DataGrid.hooks';
import { NumberOfDecimalPlaces } from '../DataGridDevEx/DataGrid.constants';

const AddSerialDialog = (props: ISerialDialogProps): JSX.Element => {
    const {
        classes, open, RequiredQuantity, BinLots, isBinTracked, onApply, onCancel, ProductId, WarehouseEntity, defaultBin, calculateSerialNumber, DocumentType
    } = props;
    const gridRef = React.useRef<DataGrid>();
    const [enteredQuantity, setEnteredQuantity] = React.useState(0);
    const [lines, setLines] = React.useState<IBinLotFacade[]>([]);
    const [serialNumber, setSerialNumber] = React.useState<string>('');
    const [selectedBin, setSelectedBin] = React.useState(null);
    const serialTextField = React.useRef<HTMLTextAreaElement | HTMLInputElement>();

    const [enterSerialNumbers, setEnterSerialNumbers] = React.useState(false);
    const [enterSerialNumberRangeOpen, setEnterSerialNumberRange] = React.useState(false);

    React.useEffect(
        () => {
            setEnterSerialNumbers(open);
        },
        [open]);

    React.useEffect(
        () => {
            setLines(BinLots ?? []);
        },
        [BinLots]);

    React.useEffect(
        () => { updateQuantities(); },
        [lines]);

    const confirmationActions = (): IActionItem[] => {
        return [
            {
                title: 'Ok',
                iconName: 'CheckCircle',
                disabled: !RequiredQuantity || enteredQuantity !== RequiredQuantity,
                listener: async () => {
                    if (onApply) {
                        await onApply(lines);
                    }
                }
            },
            {
                iconName: 'Cancel',
                isDefault: true,
                title: 'Cancel',
                listener: async () => {
                    if (onCancel) {
                        await onCancel();
                    }
                }
            },
            {
                title: 'New',
                iconName: 'CheckCircle',
                style: {
                    position: 'absolute',
                    left: 18,
                    float: 'unset',
                },
                listener: async () => {
                    setEnterSerialNumbers(false);
                    setEnterSerialNumberRange(true);
                }
            }];
    };

    const addLine = () => {
        if (isApplyDisabled) {
            return;
        }
        const newLine = {
            LotSerial: serialNumber,
            Quantity: 1,
            ...(selectedBin?.Details ?? {})
        };
        if (isNull(lines)) {
            setLines([newLine]);
        } else {
            const found = lines?.find((item) => item?.LotSerial?.toUpperCase() === serialNumber?.toUpperCase());
            if (isNull(found)) {
                setLines([...lines, newLine]);
            }
        }
        clearEntryData();
    };

    const undo = () => {
        setSerialNumber('');
    };

    const clearEntryData = () => {
        setSerialNumber('');

        if (serialTextField.current) {
            serialTextField.current.focus();
        }
    };

    const updateQuantities = () => {
        setEnteredQuantity(isNull(lines) ? 0 : lines.length);
    };

    const handleKeyDown = (event) => {
        if (event.keyCode === ENTER_KEY) {
            addLine();
        }
    };

    const calculateSerialNumberHandler = async (data) => {
        const response = await calculateSerialNumber({ ProductId, Warehouse: WarehouseEntity, BinLotCriteria: { ...(!isBinTracked && { Bin: '' }), ...data, DocumentType: DocumentType || undefined }, BinLots: lines ?? [] });
        setLines(Object.values(response?.LotSerial ?? []));

        setEnterSerialNumberRange(false);
        setEnterSerialNumbers(true);

    };

    async function deleteClick(_: string, row: any): Promise<void> {
        const filteredLines = lines?.filter((line) => line?.LotSerial !== row?.LotSerial);
        setLines(filteredLines ?? []);
        await gridRef.current?.instance?.refresh(true);
    }

    const fetchLines = React.useCallback(
        async () => {
            return lines;
        },
        [lines]
    );

    const linesDataSource = React.useMemo(
        () => createDataSource(
            'LotSerial',
            { fetch: fetchLines },
        ),
        [lines]
    );

    const isApplyDisabled = isNull(serialNumber.trim());

    const getModalContent = () => {
        return <div className={classNames({
            [classes.mainDiv]: !Boolean(isBinTracked),
            [classes.mainDivBin]: Boolean(isBinTracked)
        })}>
            <div className={classes.lotLine}>
                <TextField
                    value={serialNumber}
                    innerRef={serialTextField}
                    label={'Serial'}
                    onKeyDown={handleKeyDown}
                    onChange={(value) => { setSerialNumber(value); }}
                />
                {isBinTracked && <BasicLookupActionField
                    placeholder={'Bin'}
                    label={'Bin'}
                    isWarehouseProductScoped={true}
                    size={'large'}
                    lookupName={'ProductBinMaster'}
                    value={defaultBin}
                    params={{ ProductId: ProductId, Warehouse: WarehouseEntity }}
                    onSelectedItemChange={(data) => {
                        setSelectedBin(data);
                    }}
                    suppressDescription={true}
                />}
                <MUIButton
                    onClick={addLine}
                    variant={'contained'}
                    disabled={isApplyDisabled}
                    className={classNames(classes.button, isApplyDisabled && classes.disabledBtn)}
                >
                    <CheckCircleIcon style={{ color: 'green' }} />
                    APPLY
                </MUIButton>
                <MUIButton
                    onClick={undo}
                    variant={'contained'}
                    className={classNames(classes.button)}
                >
                    <CancelIcon style={{ color: 'red' }} />
                    UNDO
                </MUIButton>
            </div>
            <DynamicDataGrid
                dataGridClassName={classes.lotLineGrid}
                columns={isBinTracked ? columns : nonBinTrackedColumns}
                allowHeaderFilting={false}
                allowUpdating={false}
                allowDelete={true}
                actionButtonsVisibility={true}
                onDelete={deleteClick}
                gridProps={{
                    dataSource: linesDataSource,
                    dataGridRef: gridRef,
                    sorting: { mode: 'single' },
                    disableDrillDown: false,
                    disableEditing: false,
                    disableDelete: false,
                }}
            >
            </DynamicDataGrid >
            <Table>
                <TableBody>
                    <TableRow className={classes.bottomRow}>
                        <TableCell className={classes.headingCell}>REQUIRED</TableCell>
                        <TableCell className={classes.emphasizedCell}>{Number(RequiredQuantity || 0).toFixed(NumberOfDecimalPlaces)}</TableCell>
                        <TableCell className={classes.headingCell}>ENTERED</TableCell>
                        <TableCell className={classes.dataCell}>{Number(enteredQuantity).toFixed(NumberOfDecimalPlaces)}</TableCell>
                    </TableRow>
                </TableBody>
            </Table>
        </div>;
    };

    return (
        <div>

            {enterSerialNumberRangeOpen && <SerialNumber
                open={enterSerialNumberRangeOpen}
                defaultBin={defaultBin}
                calculateSerialNumberHandler={calculateSerialNumberHandler}
                isBinTracked={isBinTracked}
                productId={ProductId}
                warehouseId={WarehouseEntity}
                onCancel={() => {
                    setEnterSerialNumbers(true);
                    setEnterSerialNumberRange(false);
                }}
            />}
            <FormViewModal
                open={enterSerialNumbers}
                loading={false}
                title={'Enter Serial Numbers'}
                modalContent={getModalContent()}
                actions={confirmationActions()}
                dialogActionsButtons={true}
                dialogActionsShadow={false}
            />
        </div>);
};

export default withStyles(styles, { index: 1 })(AddSerialDialog);
